import { ArrowLeft, ArrowRight, Loader2, Minus, Plus } from "lucide-react";
import type React from "react";
import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import type {
  CharacterCreationChoiceNodeDnd5e,
  CharacterCreationChoiceValue,
  CharacterCreationEvaluationDnd5e,
  CharacterCreationInvalidationDnd5e,
} from "@/lib/core/schemas/character-creation";
import {
  bundleDescription,
  bundlesForBackground,
} from "@/lib/rules/dnd5e/character/equipment-bundles";
import {
  MAGIC_INITIATE_ABILITIES,
  MAGIC_INITIATE_CHOICES,
} from "@/lib/rules/dnd5e/character/feat-choices/magic-initiate";

type CharacterCreationGraphStepProps = {
  readonly evaluation: CharacterCreationEvaluationDnd5e | null;
  readonly focusNodeId?: string | null;
  readonly invalidations: readonly CharacterCreationInvalidationDnd5e[];
  readonly isLoading: boolean;
  readonly error: Error | null;
  readonly onBack: () => void;
  readonly onNext: () => void;
  readonly onSelect: (
    node: CharacterCreationChoiceNodeDnd5e,
    value: CharacterCreationChoiceValue
  ) => void;
};

const selectedValues = (
  node: CharacterCreationChoiceNodeDnd5e
): readonly string[] =>
  node.selection?.kind === "multi" ? node.selection.values : [];

const dependentArrayValue = (
  value: string | string[] | undefined
): readonly string[] => (Array.isArray(value) ? value : []);

const dependentStringValue = (value: string | string[] | undefined): string =>
  typeof value === "string" ? value : "";

const firstPendingNode = (
  evaluation: CharacterCreationEvaluationDnd5e | null,
  focusNodeId?: string | null
): CharacterCreationChoiceNodeDnd5e | null => {
  if (!evaluation) {
    return null;
  }
  const focused = focusNodeId
    ? (evaluation.nodes.find((node) => node.id === focusNodeId) ?? null)
    : null;
  if (focused) {
    return focused;
  }
  const pending = new Set(evaluation.pendingNodeIds);
  return evaluation.nodes.find((node) => pending.has(node.id)) ?? null;
};

export function CharacterCreationGraphStep({
  evaluation,
  focusNodeId,
  invalidations,
  isLoading,
  error,
  onBack,
  onNext,
  onSelect,
}: CharacterCreationGraphStepProps) {
  const node = firstPendingNode(evaluation, focusNodeId);

  if (isLoading && evaluation === null) {
    return (
      <GraphShell onBack={onBack} title="Choices">
        <div className="flex items-center justify-center gap-2 text-muted-foreground text-sm">
          <Loader2 className="h-4 w-4 animate-spin" />
          Evaluating choices...
        </div>
      </GraphShell>
    );
  }

  if (error) {
    return (
      <GraphShell onBack={onBack} title="Choices">
        <p className="text-destructive text-sm">{error.message}</p>
      </GraphShell>
    );
  }

  if (evaluation?.status === "invalid") {
    return (
      <GraphShell onBack={onBack} title="Choices">
        <div className="space-y-2 text-destructive text-sm">
          {evaluation.errors.map((entry) => (
            <p key={`${entry.nodeId ?? "base"}:${entry.code}`}>
              {entry.detail ?? entry.code}
            </p>
          ))}
        </div>
      </GraphShell>
    );
  }

  if (!node) {
    return (
      <GraphShell onBack={onBack} title="Choices">
        <InvalidationList invalidations={invalidations} />
        <p className="text-muted-foreground text-sm">
          Character choices are complete.
        </p>
        <Button className="w-40" onClick={onNext} type="button">
          Continue
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </GraphShell>
    );
  }

  return (
    <GraphShell onBack={onBack} title={node.label}>
      <InvalidationList invalidations={invalidations} />
      <NodeRenderer
        backgroundId={evaluation?.normalizedDraft.backgroundId}
        node={node}
        onSelect={(value) => onSelect(node, value)}
      />
    </GraphShell>
  );
}

function GraphShell({
  children,
  onBack,
  title,
}: {
  readonly children: React.ReactNode;
  readonly onBack: () => void;
  readonly title: string;
}) {
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-6 px-4 py-8">
      <div className="max-w-sm text-center">
        <h2 className="font-semibold text-xl">{title}</h2>
      </div>
      <div className="flex w-full max-w-sm flex-col gap-4">{children}</div>
      <Button onClick={onBack} type="button" variant="outline">
        <ArrowLeft className="mr-2 h-4 w-4" />
        Back
      </Button>
    </div>
  );
}

function InvalidationList({
  invalidations,
}: {
  readonly invalidations: readonly CharacterCreationInvalidationDnd5e[];
}) {
  if (invalidations.length === 0) {
    return null;
  }
  return (
    <div className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-amber-900 text-sm dark:text-amber-200">
      {invalidations.map((entry) => (
        <p key={`${entry.nodeId}:${entry.code}`}>
          {entry.detail ?? "A previous selection is no longer legal."}
        </p>
      ))}
    </div>
  );
}

function NodeRenderer({
  backgroundId,
  node,
  onSelect,
}: {
  readonly backgroundId?: string | null;
  readonly node: CharacterCreationChoiceNodeDnd5e;
  readonly onSelect: (value: CharacterCreationChoiceValue) => void;
}) {
  if (node.kind === "single") {
    return <SingleChoiceNode node={node} onSelect={onSelect} />;
  }
  if (node.kind === "multi") {
    return <MultiChoiceNode node={node} onSelect={onSelect} />;
  }
  if (node.kind === "ability-allocation") {
    return <AbilityAllocationNode node={node} onSelect={onSelect} />;
  }
  if (node.kind === "dependent") {
    return <DependentChoiceNode node={node} onSelect={onSelect} />;
  }
  if (node.kind === "equipment") {
    return (
      <EquipmentChoiceNode
        backgroundId={backgroundId}
        node={node}
        onSelect={onSelect}
      />
    );
  }
  return null;
}

function SingleChoiceNode({
  node,
  onSelect,
}: {
  readonly node: CharacterCreationChoiceNodeDnd5e;
  readonly onSelect: (value: CharacterCreationChoiceValue) => void;
}) {
  return (
    <div className="flex flex-col gap-2">
      {node.options.map((option) => (
        <Button
          className="h-auto justify-start whitespace-normal px-4 py-4 text-left"
          disabled={option.disabledReason !== undefined}
          key={option.id}
          onClick={() => onSelect({ kind: "single", value: option.id })}
          type="button"
          variant={
            node.selection?.kind === "single" &&
            node.selection.value === option.id
              ? "default"
              : "outline"
          }
        >
          {option.label}
        </Button>
      ))}
    </div>
  );
}

function MultiChoiceNode({
  node,
  onSelect,
}: {
  readonly node: CharacterCreationChoiceNodeDnd5e;
  readonly onSelect: (value: CharacterCreationChoiceValue) => void;
}) {
  const [values, setValues] = useState<readonly string[]>(selectedValues(node));
  useEffect(() => {
    setValues(selectedValues(node));
  }, [node]);
  const count = node.count ?? 1;
  const selected = new Set(values);
  const submitDisabled = values.length !== count;

  return (
    <div className="flex flex-col gap-4">
      <p className="text-muted-foreground text-sm">
        {values.length} / {count} selected
      </p>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {node.options.map((option) => {
          const isSelected = selected.has(option.id);
          return (
            <Button
              disabled={
                option.disabledReason !== undefined ||
                (!isSelected && values.length >= count)
              }
              key={option.id}
              onClick={() =>
                setValues((current) =>
                  isSelected
                    ? current.filter((entry) => entry !== option.id)
                    : [...current, option.id]
                )
              }
              type="button"
              variant={isSelected ? "default" : "outline"}
            >
              {option.label}
            </Button>
          );
        })}
      </div>
      <Button
        disabled={submitDisabled}
        onClick={() => onSelect({ kind: "multi", values: [...values] })}
        type="button"
      >
        Continue
        <ArrowRight className="ml-2 h-4 w-4" />
      </Button>
    </div>
  );
}

function AbilityAllocationNode({
  node,
  onSelect,
}: {
  readonly node: CharacterCreationChoiceNodeDnd5e;
  readonly onSelect: (value: CharacterCreationChoiceValue) => void;
}) {
  const [values, setValues] = useState<Record<string, number>>(
    node.selection?.kind === "ability-allocation" ? node.selection.values : {}
  );
  const total = Object.values(values).reduce((sum, value) => sum + value, 0);
  const count = node.count ?? 0;
  const options = node.options.length > 0 ? node.options : [];

  const adjust = (id: string, delta: 1 | -1) => {
    setValues((current) => {
      const nextValue = (current[id] ?? 0) + delta;
      const next = { ...current };
      if (nextValue <= 0) {
        delete next[id];
      } else {
        next[id] = nextValue;
      }
      return next;
    });
  };

  return (
    <div className="flex flex-col gap-4">
      <p className="text-muted-foreground text-sm">
        {total} / {count} assigned
      </p>
      <div className="flex flex-col gap-2">
        {options.map((option) => {
          const value = values[option.id] ?? 0;
          return (
            <div
              className="flex items-center justify-between rounded-md border border-input px-3 py-2"
              key={option.id}
            >
              <span className="text-sm">{option.label}</span>
              <div className="flex items-center gap-2">
                <Button
                  aria-label={`Decrease ${option.label}`}
                  disabled={value <= 0}
                  onClick={() => adjust(option.id, -1)}
                  size="icon"
                  type="button"
                  variant="outline"
                >
                  <Minus className="h-4 w-4" />
                </Button>
                <span className="w-6 text-center tabular-nums">{value}</span>
                <Button
                  aria-label={`Increase ${option.label}`}
                  disabled={total >= count || value >= 2}
                  onClick={() => adjust(option.id, 1)}
                  size="icon"
                  type="button"
                  variant="outline"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>
          );
        })}
      </div>
      <Button
        disabled={total !== count}
        onClick={() => onSelect({ kind: "ability-allocation", values })}
        type="button"
      >
        Continue
        <ArrowRight className="ml-2 h-4 w-4" />
      </Button>
    </div>
  );
}

function DependentChoiceNode({
  node,
  onSelect,
}: {
  readonly node: CharacterCreationChoiceNodeDnd5e;
  readonly onSelect: (value: CharacterCreationChoiceValue) => void;
}) {
  const selectedDependent =
    node.selection?.kind === "dependent" ? node.selection : null;
  const [source, setSource] = useState(selectedDependent?.source ?? "");
  const [cantrips, setCantrips] = useState<readonly string[]>(
    dependentArrayValue(selectedDependent?.values.cantrips)
  );
  const [spell, setSpell] = useState(
    dependentStringValue(selectedDependent?.values.spell)
  );
  const [ability, setAbility] = useState(
    dependentStringValue(selectedDependent?.values.ability)
  );
  const selectedSource = useMemo(
    () => MAGIC_INITIATE_CHOICES.find((entry) => entry.id === source) ?? null,
    [source]
  );
  const abilities = [...MAGIC_INITIATE_ABILITIES].sort();
  const canSubmit =
    selectedSource !== null &&
    cantrips.length === 2 &&
    spell.length > 0 &&
    ability.length > 0;

  useEffect(() => {
    if (node.selection?.kind !== "dependent") {
      setSource("");
      setCantrips([]);
      setSpell("");
      setAbility("");
      return;
    }
    setSource(node.selection.source);
    setCantrips(dependentArrayValue(node.selection.values.cantrips));
    setSpell(dependentStringValue(node.selection.values.spell));
    setAbility(dependentStringValue(node.selection.values.ability));
  }, [node.selection]);

  return (
    <div className="flex flex-col gap-4">
      <SingleChoiceButtons
        onSelect={(next) => {
          setSource(next);
          setCantrips([]);
          setSpell("");
        }}
        options={node.options}
        selected={source}
      />
      {selectedSource && (
        <>
          <div className="space-y-2">
            <h4 className="font-medium text-sm">
              Choose 2 Cantrips ({cantrips.length}/2)
            </h4>
            <MultiButtons
              count={2}
              onChange={setCantrips}
              options={selectedSource.cantrips}
              selected={cantrips}
            />
          </div>
          <div className="space-y-2">
            <h4 className="font-medium text-sm">Choose a 1st-Level Spell</h4>
            <SingleChoiceButtons
              onSelect={setSpell}
              options={selectedSource.firstLevelSpells}
              selected={spell}
            />
          </div>
          <div className="space-y-2">
            <h4 className="font-medium text-sm">Choose Spellcasting Ability</h4>
            <SingleChoiceButtons
              onSelect={setAbility}
              options={abilities.map((entry) => ({
                id: entry,
                label: entry.toUpperCase(),
              }))}
              selected={ability}
            />
          </div>
        </>
      )}
      <Button
        disabled={!canSubmit}
        onClick={() =>
          onSelect({
            kind: "dependent",
            source,
            values: { cantrips: [...cantrips], spell, ability },
          })
        }
        type="button"
      >
        Continue
        <ArrowRight className="ml-2 h-4 w-4" />
      </Button>
    </div>
  );
}

function EquipmentChoiceNode({
  backgroundId,
  node,
  onSelect,
}: {
  readonly backgroundId?: string | null;
  readonly node: CharacterCreationChoiceNodeDnd5e;
  readonly onSelect: (value: CharacterCreationChoiceValue) => void;
}) {
  const selectedEquipment =
    node.selection?.kind === "equipment" ? node.selection : null;
  const [classBundle, setClassBundle] = useState(
    selectedEquipment?.classBundle ?? ""
  );
  const [backgroundBundle, setBackgroundBundle] = useState(
    selectedEquipment?.backgroundBundle ?? ""
  );
  const backgroundOptions = bundlesForBackground(backgroundId).map(
    (bundle, index) => ({
      id: String.fromCharCode("a".charCodeAt(0) + index),
      label: `Background Bundle ${String.fromCharCode(
        "A".charCodeAt(0) + index
      )}`,
      description: bundleDescription(bundle.items, bundle.goldGp),
    })
  );
  const canSubmit =
    classBundle.length > 0 &&
    (backgroundOptions.length === 0 || backgroundBundle.length > 0);

  useEffect(() => {
    setClassBundle(selectedEquipment?.classBundle ?? "");
    setBackgroundBundle(selectedEquipment?.backgroundBundle ?? "");
  }, [selectedEquipment]);

  return (
    <div className="flex flex-col gap-4">
      <EquipmentOptionGroup
        onSelect={setClassBundle}
        options={node.options}
        selected={classBundle}
        title="Class Equipment"
      />
      {backgroundOptions.length > 0 && (
        <EquipmentOptionGroup
          onSelect={setBackgroundBundle}
          options={backgroundOptions}
          selected={backgroundBundle}
          title="Background Equipment"
        />
      )}
      <Button
        disabled={!canSubmit}
        onClick={() =>
          onSelect({
            kind: "equipment",
            classBundle,
            ...(backgroundBundle ? { backgroundBundle } : {}),
          })
        }
        type="button"
      >
        Continue
        <ArrowRight className="ml-2 h-4 w-4" />
      </Button>
      <Button
        className="h-auto justify-start whitespace-normal px-4 py-4 text-left"
        onClick={() => onSelect({ kind: "equipment" })}
        type="button"
        variant={
          node.selection?.kind === "equipment" &&
          node.selection.classBundle === undefined
            ? "default"
            : "outline"
        }
      >
        No equipment
      </Button>
    </div>
  );
}

function EquipmentOptionGroup({
  title,
  options,
  selected,
  onSelect,
}: {
  readonly title: string;
  readonly options: readonly {
    readonly id: string;
    readonly label: string;
    readonly description?: string;
  }[];
  readonly selected: string;
  readonly onSelect: (id: string) => void;
}) {
  return (
    <div className="flex flex-col gap-2">
      <h3 className="font-medium text-sm">{title}</h3>
      {options.map((option) => (
        <Button
          className="h-auto justify-start whitespace-normal px-4 py-4 text-left"
          key={option.id}
          onClick={() => onSelect(option.id)}
          type="button"
          variant={selected === option.id ? "default" : "outline"}
        >
          <span className="flex flex-col gap-1">
            <span>{option.label}</span>
            {option.description && (
              <span className="font-normal text-muted-foreground text-xs leading-snug">
                {option.description}
              </span>
            )}
          </span>
        </Button>
      ))}
    </div>
  );
}

function SingleChoiceButtons({
  options,
  selected,
  onSelect,
}: {
  readonly options: readonly { readonly id: string; readonly label: string }[];
  readonly selected: string;
  readonly onSelect: (id: string) => void;
}) {
  return (
    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
      {options.map((option) => (
        <Button
          key={option.id}
          onClick={() => onSelect(option.id)}
          type="button"
          variant={selected === option.id ? "default" : "outline"}
        >
          {option.label}
        </Button>
      ))}
    </div>
  );
}

function MultiButtons({
  count,
  options,
  selected,
  onChange,
}: {
  readonly count: number;
  readonly options: readonly { readonly id: string; readonly label: string }[];
  readonly selected: readonly string[];
  readonly onChange: (ids: readonly string[]) => void;
}) {
  const selectedSet = new Set(selected);
  return (
    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
      {options.map((option) => {
        const isSelected = selectedSet.has(option.id);
        return (
          <Button
            disabled={!isSelected && selected.length >= count}
            key={option.id}
            onClick={() =>
              onChange(
                isSelected
                  ? selected.filter((entry) => entry !== option.id)
                  : [...selected, option.id]
              )
            }
            type="button"
            variant={isSelected ? "default" : "outline"}
          >
            {option.label}
          </Button>
        );
      })}
    </div>
  );
}
